import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Lock,
  Mail,
  KeyRound,
  ShieldCheck,
  AlertCircle,
  Loader2,
  CheckCircle2,
  HelpCircle,
} from "lucide-react";

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, resetPassword, isConfigured } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStatus, setForgotStatus] = useState({ loading: false, msg: "", error: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password) {
      setError("Please enter both email and password.");
      return;
    }

    setLoading(true);
    try {
      if (!isConfigured) {
        throw new Error("Firebase configuration is missing in frontend/.env.");
      }

      const { profile } = await login(email, password);
      if (!profile?.role) {
        setError("This Firebase account is not authorized for the Masterclass portal.");
        return;
      }
      if (profile?.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/student/dashboard");
      }
    } catch (err) {
      console.error("Login failed:", err);
      if (err.code === "auth/invalid-credential" || err.code === "auth/user-not-found" || err.code === "auth/wrong-password") {
        setError("Invalid email or password. Please check your credentials.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many failed attempts. Please reset your password or try again later.");
      } else {
        setError(err.message || "Failed to log in. Please check your network and credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      setForgotStatus({ loading: false, msg: "", error: "Please provide your email address." });
      return;
    }

    setForgotStatus({ loading: true, msg: "", error: "" });
    try {
      await resetPassword(forgotEmail);
      setForgotStatus({
        loading: false,
        msg: "Firebase accepted the password-reset request. Check Inbox, Spam/Junk and Promotions for the Firebase password reset email.",
        error: "",
      });
    } catch (err) {
      console.error("Reset password error:", err);
      let message = err.message || "Could not send reset email.";
      if (err.code === "auth/invalid-email") message = "Please enter a valid email address.";
      if (err.code === "auth/too-many-requests") message = "Too many reset requests. Please wait a while and try again.";
      setForgotStatus({ loading: false, msg: "", error: message });
    }
  };

  return (
    <div className="min-h-screen bg-background py-16 px-4 flex items-center justify-center">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 shadow-xl space-y-6">
        <div className="text-center space-y-2">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 mx-auto flex items-center justify-center">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Portal Authentication</h1>
          <p className="text-xs text-muted-foreground font-mono">
            Kian Technologies Cyber Security Masterclass Portal
          </p>
        </div>

        {error && (
          <div
            data-testid="login-error-alert"
            className="p-3.5 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-xs flex items-start gap-2"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span className="leading-snug">{error}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-xs font-mono font-medium text-foreground mb-1">
              Registered Email Address
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 text-muted-foreground absolute left-3 top-3" />
              <input
                type="email"
                data-testid="login-email-input"
                required
                placeholder="student@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-mono font-medium text-foreground">
                Password
              </label>
              <button
                type="button"
                data-testid="forgot-password-btn"
                onClick={() => {
                  setForgotEmail(email);
                  setShowForgotModal(true);
                }}
                className="text-[11px] font-mono text-emerald-400 hover:underline"
              >
                Forgot Password?
              </button>
            </div>
            <div className="relative">
              <KeyRound className="w-4 h-4 text-muted-foreground absolute left-3 top-3" />
              <input
                type="password"
                data-testid="login-password-input"
                required
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full py-2.5 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-mono text-sm font-semibold shadow-md transition-all flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Authenticating...</span>
              </>
            ) : (
              <span>Login</span>
            )}
          </button>
        </form>

        <div className="border-t border-border pt-4 text-center space-y-2">
          <p className="text-xs text-muted-foreground font-mono">
            New Student?{" "}
            <Link
              to="/register"
              className="text-emerald-400 hover:underline font-semibold"
            >
              Apply for 15 Days Masterclass →
            </Link>
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-card border border-border max-w-sm w-full rounded-xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm">Reset Account Password</h3>
              <button
                onClick={() => setShowForgotModal(false)}
                className="text-muted-foreground hover:text-foreground text-xs font-mono"
              >
                ✕ Close
              </button>
            </div>

            <p className="text-xs text-muted-foreground">
              Enter your registered email address to receive a secure Firebase password reset link. New approved students should use the private activation code from their registration.
            </p>

            {forgotStatus.msg && (
              <div className="p-3 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                <span>{forgotStatus.msg}</span>
              </div>
            )}

            {forgotStatus.error && (
              <div className="p-3 rounded bg-destructive/10 border border-destructive/30 text-destructive text-xs">
                {forgotStatus.error}
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-3">
              <input
                type="email"
                required
                data-testid="forgot-email-input"
                placeholder="name@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
              />
              <button
                type="submit"
                disabled={forgotStatus.loading}
                data-testid="send-reset-btn"
                className="w-full py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-mono text-xs font-medium transition-colors flex items-center justify-center gap-2"
              >
                {forgotStatus.loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Sending Link...</span>
                  </>
                ) : (
                  <span>Send Reset Email</span>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
